import { useCallback } from 'react'

import {
  useMarkAllNotificationsReadMutation,
  useMarkNotificationsReadMutation,
  useMarkNotificationsUnreadMutation,
} from 'api/notification-center/notificationCenter'
import { handleError } from 'utils/error'

import { markLocallyUnread } from './notificationArrivals'
import { MARK_ALL_READ_CACHE_KEY } from './notificationCenterConstants'

export type NotificationReadState = {
  markAsRead: (ids: string[]) => void
  markAsUnread: (ids: string[]) => void
  markEverythingRead: () => void
  /** Shared through `fixedCacheKey`, so the bell and the page show the same pending state. */
  isMarkingEverythingRead: boolean
  isUpdatingReadState: boolean
}

/**
 * Read state for every surface that changes it. The three call sites had drifted into three different
 * error stories — one caught, two swallowed — and the read-state endpoints already invalidate the list
 * and the count, so nothing here patches caches by hand.
 */
export const useNotificationReadState = (): NotificationReadState => {
  const [markRead, { isLoading: isMarkingRead }] = useMarkNotificationsReadMutation()
  const [markUnread, { isLoading: isMarkingUnread }] = useMarkNotificationsUnreadMutation()
  const [markAllRead, { isLoading: isMarkingEverythingRead }] = useMarkAllNotificationsReadMutation({
    fixedCacheKey: MARK_ALL_READ_CACHE_KEY,
  })

  const markAsRead = useCallback(
    (ids: string[]) => {
      if (ids.length > 0) markRead({ ids }).unwrap().catch(handleError)
    },
    [markRead]
  )

  const markAsUnread = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return
      // Deliberately unread is not the same as newly arrived, so the subscription must not announce it.
      markLocallyUnread(ids)
      markUnread({ ids }).unwrap().catch(handleError)
    },
    [markUnread]
  )

  const markEverythingRead = useCallback(() => {
    markAllRead().unwrap().catch(handleError)
  }, [markAllRead])

  return {
    markAsRead,
    markAsUnread,
    markEverythingRead,
    isMarkingEverythingRead,
    isUpdatingReadState: isMarkingRead || isMarkingUnread,
  }
}

export default useNotificationReadState
